import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import {
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { DialogWithScrollReset as Dialog } from "@/components/ui/dialog-with-scroll-reset";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDate } from "@/lib/utils";
import { User, Users, CalendarClock, Layers, Package } from "lucide-react";

type TeamDetailsDialogProps = {
  teamId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onManageMembers?: (teamId: number, teamName: string) => void;
};

export default function TeamDetailsDialog({
  teamId,
  isOpen,
  onClose,
  onManageMembers,
}: TeamDetailsDialogProps) {
  const [activeTab, setActiveTab] = useState("overview");

  // Fetch team details
  const { data: team, isLoading: isLoadingTeam } = useQuery({
    queryKey: ['/api/teams', teamId],
    enabled: isOpen && teamId !== null,
    queryFn: async () => {
      if (!teamId) return null;
      try {
        console.log(`Fetching details for team ${teamId}`);
        const response = await apiRequest(`/api/teams/${teamId}`);
        console.log(`Team ${teamId} details response:`, response);
        return response;
      } catch (error) {
        console.error(`Error fetching details for team ${teamId}:`, error);
        throw error;
      }
    },
  });

  // Fetch team members
  const { data: members = [], isLoading: isLoadingMembers } = useQuery({
    queryKey: ['/api/teams', teamId, 'members'],
    enabled: isOpen && teamId !== null,
    queryFn: async () => {
      if (!teamId) return [];
      try {
        console.log(`Fetching members for team ${teamId}`);
        const response = await apiRequest(`/api/teams/${teamId}/members`);
        console.log(`Team ${teamId} members response:`, response);
        return response;
      } catch (error) {
        console.error(`Error fetching members for team ${teamId}:`, error);
        throw error;
      }
    },
  });

  // Fetch team licenses
  const { data: licenses = [], isLoading: isLoadingLicenses } = useQuery({
    queryKey: ['/api/teams', teamId, 'licenses'],
    enabled: isOpen && teamId !== null,
    queryFn: async () => {
      if (!teamId) return [];
      try {
        console.log(`Fetching licenses for team ${teamId}`);
        const response = await apiRequest(`/api/teams/${teamId}/licenses`);
        console.log(`Team ${teamId} licenses response:`, response);
        return response;
      } catch (error) {
        console.error(`Error fetching licenses for team ${teamId}:`, error);
        throw error;
      }
    },
  });

  const isLoading = isLoadingTeam || isLoadingMembers || isLoadingLicenses;

  const handleManageMembers = () => {
    if (onManageMembers && teamId && team) {
      onClose();
      onManageMembers(teamId, team.name);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] w-[calc(100%-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2 break-words">
            <Users className="h-5 w-5 flex-shrink-0" />
            {isLoading ? <Skeleton className="h-7 w-40" /> : team?.name}
          </DialogTitle>
          <DialogDescription>
            {isLoading ? (
              <Skeleton className="h-4 w-60 mt-1" />
            ) : (
              `Team details and management`
            )}
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="overview" className="flex-1 flex flex-col" value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="overview" className="text-xs sm:text-sm">
              <span className="hidden sm:inline">Overview</span>
              <span className="sm:hidden">Info</span>
            </TabsTrigger>
            <TabsTrigger value="members" className="text-xs sm:text-sm">
              <span className="hidden sm:inline">Members</span>
              <span className="sm:hidden">Members</span>
              <span className="ml-1">({members.length})</span>
            </TabsTrigger>
            <TabsTrigger value="licenses" className="text-xs sm:text-sm">
              <span className="hidden sm:inline">Licenses</span>
              <span className="sm:hidden">Lic.</span>
              <span className="ml-1">({licenses.length})</span>
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="flex-1 overflow-hidden">
            <ScrollArea className="h-[400px] pr-4">
              {isLoading ? (
                <div className="space-y-4">
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                  <Skeleton className="h-24 w-full" />
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Team Information</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <dt className="text-sm font-medium text-muted-foreground">Name</dt>
                          <dd className="mt-1 text-sm">{team?.name}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-muted-foreground">Status</dt>
                          <dd className="mt-1">
                            <Badge variant={team?.status === "active" ? "default" : "secondary"}>
                              {team?.status === "active" ? "Active" : "Inactive"}
                            </Badge>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-muted-foreground">Created</dt>
                          <dd className="mt-1 text-sm flex items-center gap-1">
                            <CalendarClock className="h-3 w-3" />
                            {team?.createdAt ? formatDate(new Date(team.createdAt)) : "Unknown"}
                          </dd>
                        </div>
                      </dl>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Description</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm">
                        {team?.description || "No description provided."}
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Team Statistics</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <dt className="text-sm font-medium text-muted-foreground">Members</dt>
                          <dd className="mt-1 text-sm flex items-center gap-1">
                            <User className="h-3 w-3" />
                            {members.length}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-muted-foreground">Assigned Licenses</dt>
                          <dd className="mt-1 text-sm flex items-center gap-1">
                            <Layers className="h-3 w-3" />
                            {licenses.length}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-muted-foreground">Applications</dt>
                          <dd className="mt-1 text-sm flex items-center gap-1">
                            <Package className="h-3 w-3" />
                            {new Set(licenses.map((license: any) => license.applicationId)).size}
                          </dd>
                        </div>
                      </dl>
                    </CardContent>
                  </Card>
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Members Tab */}
          <TabsContent value="members" className="flex-1 overflow-hidden">
            <ScrollArea className="h-[400px] pr-4">
              {isLoadingMembers ? (
                <div className="space-y-4">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : members.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 bg-muted/20 rounded-lg">
                  <Users className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No team members found</p>
                  <Button onClick={handleManageMembers} variant="outline" className="mt-4">
                    Add Team Members
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {members.map((member: any) => (
                    <div
                      key={member.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 rounded-md border bg-card"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                          <User className="h-5 w-5 text-slate-600" />
                        </div>
                        <div className="overflow-hidden">
                          <div className="font-medium truncate">{member.fullName}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {member.email}
                            {member.jobTitle && ` • ${member.jobTitle}`}
                          </div>
                        </div>
                      </div>
                      <Badge variant="outline" className="self-start sm:self-center mt-2 sm:mt-0">{member.status || "Active"}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Licenses Tab */}
          <TabsContent value="licenses" className="flex-1 overflow-hidden">
            <ScrollArea className="h-[400px] pr-4">
              {isLoadingLicenses ? (
                <div className="space-y-4">
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                  <Skeleton className="h-16 w-full" />
                </div>
              ) : licenses.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 bg-muted/20 rounded-lg">
                  <Layers className="h-8 w-8 text-muted-foreground mb-2" />
                  <p className="text-muted-foreground">No licenses assigned to this team</p>
                  <Button variant="outline" className="mt-4" onClick={() => window.location.href = "/licenses/assign"}>
                    Assign Licenses
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {licenses.map((license: any) => (
                    <div
                      key={license.id}
                      className="flex flex-col p-3 rounded-md border bg-card"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                            <Package className="h-5 w-5 text-slate-600" />
                          </div>
                          <div className="overflow-hidden">
                            <div className="font-medium truncate">{license.name}</div>
                            <div className="text-xs text-muted-foreground truncate">
                              {license.application?.name || "Unknown Application"}
                              {license.licenseType && ` • ${license.licenseType}`}
                              {!license.licenseType && license.type && ` • ${license.type}`}
                            </div>
                            <div className="text-xs mt-1 flex items-center gap-2">
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                (license.assignedSeats || 0) < (license.totalSeats || 0) 
                                  ? 'bg-primary/10 text-primary' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {license.assignedSeats || 0}/{license.totalSeats || 0} seats used
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mt-2 sm:mt-0">
                          <Badge variant="outline">{license.status || "Active"}</Badge>
                          {license.expirationDate && (
                            <Badge 
                              variant={
                                new Date(license.expirationDate) < new Date() 
                                  ? "destructive" 
                                  : new Date(license.expirationDate) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                                  ? "secondary"
                                  : "default"
                              }
                              className="whitespace-nowrap"
                            >
                              Expires: {formatDate(new Date(license.expirationDate))}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        <DialogFooter className="border-t pt-4 mt-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleManageMembers} disabled={isLoading}>
            <Users className="h-4 w-4 mr-2" />
            Manage Team Members
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}